<template>
	<div :class="['stepper', variant]">
		<ul class="stepper__steps" ref="stepListRef">
			<li
				:class="[
					'stepper__steps-item',
					index === currentIndexActive && 'is-active',
					index < currentIndexActive && 'is-completed',
					index > currentIndexActive && 'is-uncompleted'
				]"
				v-for="(step, index) in localSteps"
				:key="index"
				@click="
					() => {
						clicNextStep(index);
					}
				"
			>
				<span class="stepper__steps-item-indicator">
					<span v-if="index >= currentIndexActive && !step.isSummary">
						{{ index + 1 }}
					</span>
					<i
						class="icon icon-checkbox"
						v-if="index < currentIndexActive || step.isSummary"
					/>
				</span>
				<div class="stepper__steps-item-txt">
					<slot :name="'step-title' + index">
						<span
							v-if="step.title"
							class="stepper__steps-item-title"
							>{{ step.title }}</span
						>
						<span
							v-if="step.subtitle"
							class="stepper__steps-item-subtitle"
							>{{ step.subtitle }}</span
						>
					</slot>
				</div>
			</li>
		</ul>
		<div class="stepper__group">
			<div class="stepper__content">
				<slot :name="'step-content' + currentIndexActive" />
				<slot
					v-if="$slots['step-content-summary'] && submitActive"
					name="step-content-summary"
				/>
			</div>
			<footer class="stepper__actions">
				<ButtonGroup
					class="stepper__actions-group"
					ref="tooltipNextButtonMessageRef"
					@mouseleave.native="displayTooltip = false"
					@mouseover.native="activateTooltipOnNextButton"
					@mouseout.native="displayTooltip = false"
				>
					<template #container>
						<Button
							className="btn--has-icon"
							iconClassName="icon-arrow-left"
							:iconBeforeLabel="true"
							labelText="Previous"
							v-if="previousActive"
							:btnClick="goToPreviousStep"
						/>
						<Button
							className="btn--primary btn--has-icon"
							iconClassName="icon-arrow-right"
							:iconBeforeLabel="false"
							v-if="nextActive"
							:disabled="canDisabledNextStep"
							labelText="Next"
							:btnClick="emitNextStep"
						/>
						<Button
							className="btn--primary"
							v-if="submitActive"
							:disabled="!canEnableSubmit"
							:labelText="submitText"
							:btnClick="submit"
						/>
						<Button
							className="btn--link"
							v-if="cancelActive"
							labelText="Cancel"
							:btnClick="cancel"
						/>
					</template>
				</ButtonGroup>
				<Tooltip
					:display="displayTooltip"
					:offset="[0, 4]"
					:text="requiredFieldsMessage"
					:trackedRefName="'tooltipNextButtonMessageRef'"
					position="top"
				/>
			</footer>
		</div>
	</div>
</template>

<script>
import ButtonGroup from '@/components/forms/button/button-group';
import Button from '@/components/forms/button/button';
import scrollNavLogic from '@/mixins/scroll-nav-logic';
import Tooltip from '@/components/shells/tooltip/tooltip';

export default {
	name: 'Stepper',
	mixins: [scrollNavLogic],
	components: {
		ButtonGroup,
		Button,
		Tooltip
	},
	props: {
		title: {
			type: String,
			default: undefined
		},
		steps: {
			type: Array,
			default: () => []
		},
		canDisabledNextStep: {
			type: Boolean,
			default: true
		},
		hasError: {
			type: Boolean,
			default: false
		},
		canEnableSubmit: {
			type: Boolean,
			default: true
		},
		submitText: {
			type: String,
			default: 'Submit'
		},
	},
	data() {
		return {
			localSteps: [...this.steps],
			requiredFieldsMessage: 'Complete required fields to proceed',
			displayTooltip: false
		};
	},
	methods: {
		activateTooltipOnNextButton() {
			if (this.canDisabledNextStep) this.displayTooltip = true;
			else this.displayTooltip = false;
		},
		clicNextStep(index) {
			if (!this.hasError && !this.canDisabledNextStep)
				this.updateCurrentIndexActive(index);

			if (index < this.currentIndexActive)
				this.updateCurrentIndexActive(index);
		},
		submit() {
			this.$emit('submit');
		},
		cancel() {
			this.$emit('cancel');
		},
		emitNextStep() {
			if (!this.hasError) this.goToNextStep();
			else {
				this.$emit('currentIndexActive', this.currentIndexActive);
				this.$emit('next');
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/* BASE */
.stepper {
	display: flex;
	flex-direction: column;
	background-color: $white;
	padding: 0 $padding-xl $padding-md $padding-xl;

	&__steps {
		display: flex;
		justify-content: center;
		background-color: $white;
        position: sticky;
        top: 0px;
        padding: $padding-md 0 $padding-md 0;
        z-index: 2;
	}

	&__steps-item {
		cursor: pointer;
		position: relative;
		display: flex;
		flex: auto;
		max-width: calc(100% / 6);
		flex-direction: column;
		font-weight: $medium-font-weight;
		text-align: center;

		&::after {
			position: absolute;
			content: '';
			width: 100%;
			height: 3px;
			top: 15px;
			background-color: $neutral-4;
		}

		&:first-child::after {
			width: 50%;
			left: 50%;
		}

		&:last-child::after {
			width: 50%;
			right: 50%;
		}
	}

	&__steps-item-indicator {
		display: flex;
		width: 30px;
		height: 30px;
		z-index: 1;
		align-self: center;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		font-size: $bd-txt-font-size-lg;

		.icon {
			font-size: 14px;
		}
	}
	&__steps-item-txt {
		display: flex;
		flex-direction: column;
		margin-top: $gutter-md;
	}
	&__steps-item-title {
		width: calc(100% - 32px);
		font-size: $bd-txt-font-size-regular;
		align-self: center;
	}

	&__steps-item-subtitle {
		width: calc(100% - 16px);
		margin-top: $gutter-sm;
		font-size: $bd-txt-font-size-sm;
		font-weight: $default-font-weight;
		align-self: center;
	}

	&__content {
		margin-top: $gutter-xl;
	}

	&__actions {
		display: flex;
		justify-content: flex-end;
		margin-top: $gutter-xl;
	}
}

/* VERTICAL */
.stepper--vertical {
	flex-direction: row;

	.stepper__steps {
		flex-direction: column;
		justify-content: flex-start;
	}

	.stepper__steps-item {
		flex: initial;
		max-width: initial;
		width: 175px;
		display: grid;
		grid-template-columns: 30px 1fr;
		column-gap: $gutter-lg;
		text-align: left;
		padding-bottom: 56px;

		&:last-child {
			padding-bottom: 0;
		}

		&::after {
			width: 3px;
			height: 100%;
			top: 0;
			left: 15px;
		}

		&:first-child::after {
			height: 100%;
		}

		&:last-child::after {
			height: 0;
		}
	}
	.stepper__steps-item-indicator {
		align-self: flex-start;
	}
	.stepper__steps-item-title {
		width: auto;
		line-height: 1.2;
		align-self: auto;
	}

	.stepper__steps-item-subtitle {
		width: auto;
		align-self: auto;
		margin-top: 2px;
	}
	.stepper__group {
		display: flex;
		flex-direction: column;
		padding-left: $padding-xl;
		justify-content: space-between;
	}
	.stepper__content {
		margin-top: 0;
	}
}

/* STATES */
.stepper__steps-item {
	&.is-active {
		.stepper__steps-item-indicator {
			background-color: $base;
			color: $white;
		}
	}

	&.is-completed {
		color: $base;
		.stepper__steps-item-indicator {
			background-color: $accent;
			color: $white;
		}

		&:hover {
			.stepper__steps-item-indicator {
				background-color: $base-bkgd-dark;
			}
		}
	}

	&.is-uncompleted {		
		.stepper__steps-item-indicator {
			background-color: $white;
			border: 3px solid $neutral-4;
		}
		color: $neutral-3;

		&:hover {
			color: $black;
			.stepper__steps-item-indicator {
				background-color: $base-bkgd-dark;
				border: none;
				color: $white;
			}			
		}
	}
}
</style>
